import React from "react";

import { useFeatureGate } from "@statsig/react-bindings";

import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import { IntegrationProviderId } from "@app/common/types";
import Config from "@app/config";

/**
 * Types
 */
type CheckIfProviderIsConnected = (
  providerId?: IntegrationProviderId
) => boolean;

export interface ProviderOption<P> {
  checkIfProviderIsConnected?: CheckIfProviderIsConnected;
  props: Omit<P, "className">;
  Component: React.FC<P>;
}

/**
 * Constants
 */
const MAX_PROVIDERS_SHOWN = 4;

const PROVIDER_PRIORITY: IntegrationProviderId[] = ["slack_bot", "hubspot"];

const PROVIDER_PRIORITY_WITH_SALESFORCE: IntegrationProviderId[] = [
  "slack_bot",
  "hubspot",
  "salesforce",
];

/**
 * A generic check function to be used in most provider options below to check if the provider
 * is connected or not.
 *
 * @param providerId The provider to check
 * @returns {boolean} Whether the providerId is connected or not
 */
export const genericProviderCheck: CheckIfProviderIsConnected = (
  providerId?: IntegrationProviderId
) => {
  if (!providerId) {
    return false;
  }

  const { isProviderConnected } = useIntegrations();

  const isIntegrationEnabled = isProviderConnected({
    providerId,
  });

  return isIntegrationEnabled;
};

/**
 * Takes a set of provider options and determines which should be shown in the nav and which
 * should be shown in the menu.
 *
 * @param providerOptions The set of provider options to utilize for the hook
 * @returns The options to show in the nav, menu, and all the options available
 */
export const useProviderOptions = <P extends {}>(
  providerOptions: Partial<Record<IntegrationProviderId, ProviderOption<P>>>
) => {
  const salesforceEnabled =
    useFeatureGate("enable_salesforce").value ||
    Config.flags.enableSalesforceIntegration;

  const providerPriority = salesforceEnabled
    ? PROVIDER_PRIORITY_WITH_SALESFORCE
    : PROVIDER_PRIORITY;

  const unsortedOptions = providerPriority
    .map((providerId) => {
      if (providerOptions[providerId]) {
        const isProviderConnected =
          (
            providerOptions[providerId]?.checkIfProviderIsConnected ??
            genericProviderCheck
          )(providerId) ?? false;

        return {
          providerId,
          option: providerOptions[providerId],
          isProviderConnected,
        };
      }

      return {
        providerId,
        option: null,
        isProviderConnected: false,
      };
    })
    .filter(({ option }) => Boolean(option)) as {
    option: ProviderOption<P>;
    providerId: IntegrationProviderId;
    isProviderConnected: boolean;
  }[];

  const options = unsortedOptions.sort((a, b) => {
    if (a.isProviderConnected && !b.isProviderConnected) {
      return -1;
    } else if (!a.isProviderConnected && b.isProviderConnected) {
      return 1;
    }

    return 0;
  });

  const optionsInNav = options.slice(0, MAX_PROVIDERS_SHOWN);
  const optionsInMenu = options.slice(MAX_PROVIDERS_SHOWN);

  return { optionsInNav, optionsInMenu, options };
};
